import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface InlineDossierProps {
  analysis: Record<string, unknown> | null;
  strategy: Record<string, unknown> | null;
}

const getString = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  return null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

export const InlineDossier = ({ analysis, strategy }: InlineDossierProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  if (!analysis && !strategy) return <></>;

  const analysisRecord = asRecord(analysis);
  const languageRecord = asRecord(analysisRecord?.language);
  const sentimentRecord = asRecord(analysisRecord?.sentiment);
  const leadProfileRecord = asRecord(analysisRecord?.leadProfile);

  const language = getString(languageRecord?.detected) || getString(languageRecord?.language);
  const sentiment = getString(sentimentRecord?.sentiment);
  const seniority = getString(leadProfileRecord?.seniority);
  const industry = getString(leadProfileRecord?.industry);
  const companySize = getString(leadProfileRecord?.companySize);

  const strategyRecord = asRecord(strategy);
  const methodology = getString(strategyRecord?.methodology);
  const reasoning = getString(strategyRecord?.reasoning);
  const tactics = Array.isArray(strategyRecord?.tactics)
    ? strategyRecord.tactics.filter((tactic): tactic is string => typeof tactic === 'string')
    : [];

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="text-xs font-semibold text-muted-foreground">Lead dossier</div>
        <Button variant="ghost" size="sm" onClick={() => setOpen((prev) => !prev)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </div>
      {open && (
        <div className="border-t px-3 py-3 text-xs text-muted-foreground">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <div>
                <span className="font-semibold text-foreground">Language:</span>{' '}
                {language ? (
                  <span data-content-origin="generated">{language}</span>
                ) : (
                  'Unknown'
                )}
              </div>
              <div>
                <span className="font-semibold text-foreground">Sentiment:</span>{' '}
                {sentiment ? (
                  <span data-content-origin="generated">{sentiment}</span>
                ) : (
                  'Neutral'
                )}
              </div>
              <div>
                <span className="font-semibold text-foreground">Industry:</span>{' '}
                {industry ? (
                  <span data-content-origin="generated">{industry}</span>
                ) : (
                  'N/A'
                )}
              </div>
              <div>
                <span className="font-semibold text-foreground">Company size:</span>{' '}
                {companySize ? (
                  <span data-content-origin="generated">{companySize}</span>
                ) : (
                  'N/A'
                )}
              </div>
              <div>
                <span className="font-semibold text-foreground">Seniority:</span>{' '}
                {seniority ? (
                  <span data-content-origin="generated">{seniority}</span>
                ) : (
                  'N/A'
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <span className="font-semibold text-foreground">Methodology:</span>{' '}
                {methodology ? (
                  <span data-content-origin="generated">{methodology}</span>
                ) : (
                  'N/A'
                )}
              </div>
              {reasoning && (
                <div>
                  <span className="font-semibold text-foreground">Strategy:</span>{' '}
                  <span data-content-origin="generated">{reasoning}</span>
                </div>
              )}
              {tactics.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tactics.map((tactic: string) => (
                    <Badge key={tactic} variant="outline" className="text-[10px]">
                      <span data-content-origin="generated">{tactic}</span>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
